import { useEffect, useState } from 'react';
import { Post } from '@/types/post';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAdmin } from '@/contexts/AdminContext';
import { usePosts } from '@/contexts/PostsContext';
import EmojiReaction from '@/components/base/EmojiReaction';
import CommentSection from '@/components/base/CommentSection';

const CATEGORY_COLORS: Record<string, string> = {
  'Global Affairs': 'bg-teal-600',
  'International Order': 'bg-stone-700',
  'Rights and Freedoms': 'bg-rose-500',
  'Politics': 'bg-amber-500',
  'Justice and Freedom': 'bg-neutral-600',
  'Law and Society': 'bg-stone-500',
};

interface PostModalProps {
  post: Post;
  onClose: () => void;
  onEdit?: () => void;
}

const PostModal = ({ post, onClose, onEdit }: PostModalProps) => {
  const { lang, t } = useLanguage();
  const { isAdmin } = useAdmin();
  const { posts, toggleComments, deletePost, trackPostView, togglePostPublic } = usePosts();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  // Always use the latest live post data from context
  const livePost = posts.find((p) => p.id === post.id) || post;

  const catColor = CATEGORY_COLORS[livePost.category] || 'bg-stone-600';
  const slides = livePost.slides.length > 0 ? livePost.slides : [livePost.coverImage];
  const total = slides.length;

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') setCurrentSlide((s) => Math.max(0, s - 1));
      if (e.key === 'ArrowRight') setCurrentSlide((s) => Math.min(total - 1, s + 1));
    };
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', handleKey);
    };
  }, [onClose, total]);

  useEffect(() => {
    // Track view when modal opens
    trackPostView(post.id);
  }, [post.id, trackPostView]);

  const handleTogglePublic = async () => {
    await togglePostPublic(livePost.id);
  };

  const handleCopyLink = async () => {
    const url = `${window.location.origin}/issues/${livePost.slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch {
      // Fallback
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    }
  };

  const goNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentSlide((s) => Math.min(total - 1, s + 1));
  };
  const goPrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentSlide((s) => Math.max(0, s - 1));
  };

  const handleToggleComments = async () => {
    await toggleComments(livePost.id);
  };

  const handleDeletePost = async () => {
    await deletePost(livePost.id);
    onClose();
  };

  const hasContent = !!(livePost.body[lang]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className={`relative z-10 w-full max-w-lg max-h-[92vh] rounded-2xl overflow-hidden flex flex-col ${livePost.isPublic ? 'bg-white' : 'bg-red-50/30'}`}>

        {/* Top bar */}
        <div className={`flex items-center justify-between px-4 py-3 border-b shrink-0 ${livePost.isPublic ? 'border-stone-100' : 'border-red-100'}`}>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full text-white ${catColor} whitespace-nowrap`}>
              {livePost.category}
            </span>
            {!livePost.isPublic && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-600 whitespace-nowrap">
                <i className="ri-eye-off-line mr-0.5" />
                {lang === 'ko' ? '비공개' : 'Private'}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <>
                <button
                  onClick={handleTogglePublic}
                  title={livePost.isPublic ? t('Make private', '비공개로 변경') : t('Make public', '공개로 변경')}
                  className={`flex items-center gap-1.5 text-[10px] font-semibold px-2.5 py-1 rounded-full cursor-pointer transition-colors whitespace-nowrap ${
                    livePost.isPublic
                      ? 'bg-teal-50 text-teal-700 hover:bg-teal-100'
                      : 'bg-red-50 text-red-600 hover:bg-red-100'
                  }`}
                >
                  <i className={`text-xs ${livePost.isPublic ? 'ri-eye-line' : 'ri-eye-off-line'}`} />
                  {livePost.isPublic ? t('Public', '공개') : t('Private', '비공개')}
                </button>
                {onEdit && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onEdit(); }}
                    title={t('Edit post', '게시물 수정')}
                    className="flex items-center gap-1.5 text-[10px] font-semibold px-2.5 py-1 rounded-full cursor-pointer transition-colors whitespace-nowrap bg-amber-50 text-amber-700 hover:bg-amber-100"
                  >
                    <i className="ri-edit-line text-xs" />
                    {t('Edit', '수정')}
                  </button>
                )}
                <button
                  onClick={() => toggleComments(livePost.id)}
                  title={livePost.commentsEnabled ? t('Disable comments', '댓글 비활성화') : t('Enable comments', '댓글 활성화')}
                  className={`flex items-center gap-1.5 text-[10px] font-semibold px-2.5 py-1 rounded-full cursor-pointer transition-colors whitespace-nowrap ${
                    livePost.commentsEnabled
                      ? 'bg-teal-50 text-teal-700 hover:bg-teal-100'
                      : 'bg-red-50 text-red-500 hover:bg-red-100'
                  }`}
                >
                  <i className={`text-xs ${livePost.commentsEnabled ? 'ri-chat-check-line' : 'ri-chat-off-line'}`} />
                  {livePost.commentsEnabled ? t('Comments ON', '댓글 ON') : t('Comments OFF', '댓글 OFF')}
                </button>
                {!confirmDelete ? (
                  <button
                    onClick={() => setConfirmDelete(true)}
                    title={t('Delete post', '게시물 삭제')}
                    className="w-7 h-7 flex items-center justify-center text-red-400 hover:bg-red-50 rounded-full cursor-pointer transition-colors"
                  >
                    <i className="ri-delete-bin-line text-xs" />
                  </button>
                ) : (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setConfirmDelete(false)}
                      className="text-[10px] px-2 py-1 rounded-full border border-stone-200 text-stone-500 hover:bg-stone-50 cursor-pointer whitespace-nowrap"
                    >
                      {t('Cancel', '취소')}
                    </button>
                    <button
                      onClick={handleDeletePost}
                      className="text-[10px] px-2 py-1 rounded-full bg-red-500 text-white hover:bg-red-600 cursor-pointer whitespace-nowrap"
                    >
                      {t('Delete', '삭제')}
                    </button>
                  </div>
                )}
              </>
            )}
            {/* Close + Share buttons (visible to all) */}
            <button
              onClick={handleCopyLink}
              title={t('Copy link', '링크 복사')}
              className="flex items-center gap-1.5 text-[10px] font-semibold px-2.5 py-1 rounded-full cursor-pointer transition-colors whitespace-nowrap bg-stone-100 text-stone-600 hover:bg-stone-200"
            >
              <i className={`text-xs ${copiedLink ? 'ri-check-line' : 'ri-share-line'}`} />
              {copiedLink ? t('Copied', '복사됨') : t('Share', '공유')}
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-stone-100 hover:bg-stone-200 cursor-pointer transition-colors"
            >
              <i className="ri-close-line text-stone-600 text-base" />
            </button>
          </div>
        </div>

        {/* Square Carousel */}
        <div className="relative shrink-0 bg-stone-950" style={{ paddingBottom: '100%', position: 'relative' }}>
          <div className="absolute inset-0">
            {slides.map((src, idx) => (
              <div
                key={idx}
                className={`absolute inset-0 transition-opacity duration-300 ${idx === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
              >
                <img
                  src={src}
                  alt={`${livePost.title[lang]} — slide ${idx + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}

            {total > 1 && (
              <>
                <button
                  onClick={goPrev}
                  disabled={currentSlide === 0}
                  className={`absolute left-3 top-1/2 -translate-y-1/2 z-30 w-9 h-9 flex items-center justify-center rounded-full bg-black/50 backdrop-blur-sm text-white cursor-pointer transition-all ${currentSlide === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-black/70'}`}
                >
                  <i className="ri-arrow-left-s-line text-lg" />
                </button>
                <button
                  onClick={goNext}
                  disabled={currentSlide === total - 1}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 z-30 w-9 h-9 flex items-center justify-center rounded-full bg-black/50 backdrop-blur-sm text-white cursor-pointer transition-all ${currentSlide === total - 1 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-black/70'}`}
                >
                  <i className="ri-arrow-right-s-line text-lg" />
                </button>
              </>
            )}

            {total > 1 && (
              <div className="absolute bottom-3 left-0 right-0 z-30 flex items-center justify-center gap-1.5">
                {slides.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={(e) => { e.stopPropagation(); setCurrentSlide(idx); }}
                    className={`rounded-full cursor-pointer transition-all duration-200 ${idx === currentSlide ? 'w-4 h-1.5 bg-white' : 'w-1.5 h-1.5 bg-white/50 hover:bg-white/80'}`}
                  />
                ))}
              </div>
            )}

            {total > 1 && (
              <div className="absolute top-3 right-3 z-30 bg-black/50 backdrop-blur-sm text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">
                {currentSlide + 1} / {total}
              </div>
            )}
          </div>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-5 pt-4 pb-2">
            <h2 className="text-sm font-bold text-stone-900 leading-snug">
              {livePost.title[lang]}
            </h2>
          </div>

          <div className="px-5 pb-6 space-y-4">
            {livePost.summary[lang] && (
              <p className="text-xs text-stone-500 leading-relaxed">{livePost.summary[lang]}</p>
            )}

            {hasContent && (
              <div className="text-xs text-stone-700 leading-relaxed space-y-2">
                {livePost.body[lang].split('\n').map((line, i) =>
                  line.trim() === '' ? (
                    <div key={i} className="h-2" />
                  ) : (
                    <p key={i}>{line}</p>
                  )
                )}
              </div>
            )}

            {!hasContent && !livePost.summary[lang] && (
              <p className="text-xs text-stone-400 text-center py-4">
                {t('No additional content for this post.', '이 포스트에 추가 내용이 없습니다.')}
              </p>
            )}

            <div className="border-t border-stone-100 pt-4">
              <EmojiReaction postId={livePost.id} reactions={livePost.emojiReactions} />
            </div>

            <div className="border-t border-stone-100 pt-4">
              <CommentSection
                postId={livePost.id}
                comments={livePost.comments}
                commentsEnabled={livePost.commentsEnabled}
              />
            </div>

            <div className="pb-2" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostModal;